/* global Promise */

import React from 'react';
import PropTypes from 'prop-types';

import { showInteraction, unmountInteraction } from './interactions/InteractionDisplay';
import { showMedia, unmountMedia } from './media/MediaDisplay';
import interactionVisibilityService from '../../services/questionView/InteractionVisibilityService';
import showHiddenInteractionPlaceholder from './interactions/HiddenInteractionDisplay';
import { renderAudioElements } from './interactions/choiceInteraction/ChoiceInteraction';
import { reportError } from '../../services/ErrorReporting';
import QuestionInErrorState from './QuestionInErrorState';
import objectTypes from '../../constants/ObjectTypes';
import sessionReader from '../../store/readHelpers/SessionReader';
import customFieldsService from '../../services/CustomFieldsService';
import MathDisplay from './mathDisplay/MathDisplay';

import styles from './QTIAssessmentItem.scss';
import questionReader from '../../store/readHelpers/QuestionReader';
import uiService from '../../services/UIService';
import questionSelectionService from '../../services/QuestionSelectionService';
import feedbackService from '../../services/FeedbackService';

class QTIAssessmentItem extends React.Component {
    static propTypes = {
        /* eslint-disable react/no-unused-prop-types */
        // the callback function for writing back results callback(responseIdentifier, [response])
        callback: PropTypes.func.isRequired,

        /*
         OPTIONAL provided if the interaction supports the shuffling of choices and this is turned on.
         The choices should then be rendered in that order
         */
        choiceSequences: PropTypes.object,

        // The context in which the question is displayed. Eg: 'exam' or 'preview'.
        context: PropTypes.string.isRequired,

        // OPTIONAL the correct responses as an object with responseIdentifier as map key
        // correctResponses is only provided in read only mode when info about correct/wrong answers should be shown
        correctResponses: PropTypes.object,
        reportReady: PropTypes.func.isRequired,

        // The questions part of the interactionVisibilityState
        questionInteractionVisibility: PropTypes.object,

        isCurrent: PropTypes.bool,
        currentScorableItem: PropTypes.number,

        isPreRender: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        lockedMedia: PropTypes.bool,

        // OPTIONAL object that contains the score and feedback for the question and each of the interactions
        outcomeVariables: PropTypes.object,

        // the object output from QTIParser with {getComponentBodyAsHTML, getInteractions}
        question: PropTypes.object.isRequired,
        services: PropTypes.object.isRequired,

        // the user responses object with responseIdentifier as map key
        userResponses: PropTypes.object.isRequired,
        viewMedia: PropTypes.string,
        direction: PropTypes.string,

        onMediaPlayedToCompletion: PropTypes.func,
        onMediaPlayedCounterReset: PropTypes.func,

        feedback: PropTypes.object,
        shouldShowInteractionFeedback: PropTypes.bool,
    };

    static filterOutDuplicateMediaModels(mediaModels) {
        return mediaModels.filter((outerModel, outerIndex, arr) => {
            // Filter out duplicate media models. They will have identical targets.
            return !arr.find((innerModel, innerIndex) => {
                return innerModel.target === outerModel.target
                    && innerIndex < outerIndex;
            });
        });
    }

    static getExtraResponses(extraResponseDeclarations, userResponses) {
        if (Array.isArray(extraResponseDeclarations)) {
            const extraResponses = [];

            for (const declaration of extraResponseDeclarations) {
                const response = userResponses[declaration.identifier];
                if (response) {
                    extraResponses.push({
                        [declaration.identifier]: response,
                    });
                }
            }

            return extraResponses;
        }

        return undefined;
    }

    constructor(props) {
        super(props);

        const { question } = props;
        this.state = {
            inQuestionErrorState: (question && question.documentParseError),
        };

        this.shouldRenderAudioFromAlternatives = !interactionVisibilityService
            .shouldShowInteractions(
                props.questionInteractionVisibility,
                props.viewMedia
            );

        this.rootElement = React.createRef();
        this.prepareAudioFilesForAutoPlay = this.prepareAudioFilesForAutoPlay.bind(this);
        this.mediaMagicApplied = false;
    }

    componentDidMount() {
        // initial draw, so set up the interactions and media
        this.updateContent(this.props, true);
        if (this.shouldRenderAudioFromAlternatives) {
            document.body.addEventListener('touchstart', this.prepareAudioFilesForAutoPlay, false);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const inQuestionErrorState = (nextProps.question && nextProps.question.documentParseError);

        // Reset error state on new question or get the new props tested
        this.setState({
            inQuestionErrorState,
        });

        if (this.areNextPropsDifferent(this.props, nextProps)) {
            // Do not update content if new props are the same as old props.
            this.updateContent(nextProps, false);
        }
    }

    shouldComponentUpdate() {
        // Do not re-render the component, since his will redraw the static html added in dangerouslySetInnerHTML
        // and then the interaction components will loose their parents
        return false;
    }

    componentWillUnmount() {
        this.unmountInteractionsAndMedia();
        if (this.shouldRenderAudioFromAlternatives) {
            document.body.removeEventListener('touchstart', this.prepareAudioFilesForAutoPlay, false);
            this.prepareAudioFilesForAutoPlay();
        }
    }

    /**
     * This is called in response to a 'touchstart' event and is needed to enable automatic playing of audio files in iOS.
     */
    prepareAudioFilesForAutoPlay() {
        if (this.mediaMagicApplied || typeof this.rootElement.current !== 'object') {
            return;
        }

        Array
            .from(
                this.rootElement.current.querySelectorAll('audio')
            )
            .forEach((audioElement) => {
                audioElement.play();
                audioElement.pause();
                audioElement.currentTime = 0;
            });

        this.mediaMagicApplied = true;
    }

    isInQuestionErrorState = () => {
        return this.state.inQuestionErrorState;
    };

    areNextPropsDifferent = (currentProps, nextProps) => {
        return JSON.stringify(currentProps) !== JSON.stringify(nextProps);
    };

    handleRenderError = (error) => {
        if (this.isInQuestionErrorState()) {
            return;
        }

        console.error(error);
        this.setState({ inQuestionErrorState: true });
        // have to forceUpdate since it can be called from componentDidMount
        this.forceUpdate();
    };

    unmountInteractionsAndMedia() {
        const { responseModels = [] } = this.props.question;
        responseModels.forEach(model => unmountInteraction(model.target));

        const { mediaModels = [] } = this.props.question;
        mediaModels.forEach(model => unmountMedia(model.target));
    }

    getOutcomeValueForInteraction = (prefix, identifier, props) => {
        const { outcomeVariables } = props;
        if (outcomeVariables) {
            const outcomeObject = outcomeVariables[`${prefix}_${identifier}`];
            return outcomeObject ? outcomeObject.value : undefined;
        }
        return undefined;
    };

    reportReady = (success) => {
        try {
            const { reportReady, question } = this.props;
            if (typeof reportReady === 'function') {
                reportReady(success, question.identifier);
            } else {
                reportError(`No reportReady on ${question.contentRevisionId}, ${question.objectType}, ${question.title}`);
            }
        } catch (error) {
            console.error(error);
        }
    };

    async updateContent(props, isInitialMount) {
        const promises = [];

        if (interactionVisibilityService.shouldShowInteractions(props.questionInteractionVisibility, props.viewMedia)) {
            promises.push(
                this.updateInteractions(props)
                    .then(() => this.updateMathJax()) // MathJax must be updated after the interactions are ready.
            );
        } else {
            promises.push(
                this.showHiddenInteractionPlaceholders(props)
            );
        }

        if (isInitialMount && !sessionReader.isPrintTypeAnswersOnly()) {
            promises.push(
                this.updateMedia(props) // Media can be updated in parallel with the interactions. They handle their own media.
            );
        }

        return Promise
            .all(promises)
            .then(() => this.reportReady(true))
            .catch((error) => {
                this.reportReady(false);
                this.handleRenderError(error);
            });
    }

    async updateMathJax() {
        if (this.rootElement.current instanceof Element) {
            return MathDisplay.showMath(this.rootElement.current.firstChild);
        }
        return Promise.resolve(false);
    }

    async updateInteractions(props) {
        const {
            choiceSequences,
            context,
            correctResponses,
            isPreRender,
            services,
            userResponses,
            viewMedia,
            direction,
            shouldShowInteractionFeedback,
        } = props;
        const { responseModels } = props.question;
        const skipUnansweredIndicator = context === 'preview' && viewMedia !== 'screen';

        if (!responseModels || this.isInQuestionErrorState()) {
            return false;
        }

        const allPromises = responseModels.map((model) => {
            const { identifier } = model;
            const extraResponses = QTIAssessmentItem.getExtraResponses(model.extraResponseDeclarations, userResponses);
            const outcomeIsCorrect = this.getOutcomeValueForInteraction('isCorrect', identifier, props);
            const outcomeIsPartiallyCorrect = this.getOutcomeValueForInteraction('isPartiallyCorrect', identifier, props);
            const responseDeclaration = props.question.responseDeclarations.filter(declaration => declaration.identifier === identifier);
            const defaultValue = responseDeclaration.length ? responseDeclaration[0].defaultValue : [];
            const hasValidResponseFeedback = feedbackService.hasValidResponseFeedback(
                identifier, props.question, props.feedback, shouldShowInteractionFeedback
            );

            return showInteraction(
                model,
                props.question,
                userResponses[identifier] || [],
                this.interactionCallback,
                correctResponses ? (correctResponses[identifier] || correctResponses.evaluationTestCases) : undefined,
                choiceSequences ? choiceSequences[identifier] : undefined,
                extraResponses,
                outcomeIsCorrect,
                outcomeIsPartiallyCorrect,
                services,
                props.context,
                props.isReadOnly,
                props.isCurrent,
                props.flaggedItems,
                props.currentScorableItem,
                props.settingValues,
                props.viewMedia,
                skipUnansweredIndicator,
                isPreRender,
                defaultValue,
                direction,
                hasValidResponseFeedback,
            );
        });

        return Promise.all(allPromises);
    }

    async showHiddenInteractionPlaceholders(props) {
        const { responseModels } = props.question;
        if (!responseModels || this.isInQuestionErrorState()) {
            return false;
        }

        const allPromises = responseModels.map((model) => {
            return showHiddenInteractionPlaceholder(model);
        });

        return Promise.all(allPromises);
    }

    async updateMedia(props) {
        const {
            isPreRender,
            lockedMedia = false,
            onMediaPlayedCounterReset,
            onMediaPlayedToCompletion,
            services,
            question: { displayContext, mediaModels, objectType },
        } = props;

        if (!mediaModels || mediaModels.length === 0 || this.isInQuestionErrorState()) {
            return true;
        }

        const showMediaParams = {
            services,
            displayContext,
            mediaElement: undefined,
            isPreRender,
            lockedMedia,
            questionObjectType: objectType,
            onMediaPlayedToCompletion,
            onMediaPlayedCounterReset,
        };

        const promises = QTIAssessmentItem
            .filterOutDuplicateMediaModels(mediaModels)
            .map(model => showMedia({ ...showMediaParams, model }));

        return Promise.all(promises);
    }

    interactionCallback = (identifier, response, extraResponses) => {
        const {
            callback,
            isPreRender,
            services,
            question: { contentRevisionId },
        } = this.props;

        const shouldAutoSelectQuestion = !isPreRender
            && !sessionReader.isPublicExam()
            && !sessionReader.isContextViewOwnDelivery()
            && questionSelectionService.isQuestionInSectionWithSelectionEnabled(contentRevisionId)
            && services.questionSelectionService.isUndecided(contentRevisionId);

        if (shouldAutoSelectQuestion) {
            services.questionSelectionService.autoSelectQuestion(contentRevisionId);
        }

        callback(contentRevisionId, identifier, response, extraResponses);
    };

    /**
     * This will render audio files that belong in choice question alternatives. The purpose is to have them available for
     * "activation" via a touchstart event before they are rendered inside the interaction.
     * See touchAudioFiles()
     * @param question
     * @param choiceSequences
     * @return {null|*}
     */
    renderAudioFromChoiceAlternatives(question, choiceSequences) {
        const { responseModels } = question;
        const { identifier } = responseModels[0];
        if ([objectTypes.MULTIPLE_CHOICE, objectTypes.MULTIPLE_RESPONSE].includes(question.objectType)) {
            return renderAudioElements(responseModels[0], choiceSequences[identifier]);
        }
        return null;
    }

    render() {
        const { choiceSequences, question, services } = this.props;
        const lang = services.i18nService.getIetfLanguageCode(question.language);
        const classNames = [styles.QTIAssessmentItem, 'preRender'];

        if (questionReader.isWordFormationQuestion(question)) {
            classNames.push(styles.ExtendedRenderingWordFormation);
        }

        if (uiService.shouldAddExtraLineSpacing(question)) {
            classNames.push('extraLineSpacing');
        }

        if (sessionReader.isPrintStylePaperBased()) {
            classNames.push(styles.pbComponentBody);
            if (customFieldsService.isMatchingFeature(question) ||
                customFieldsService.isMatchingPlanMapDiagramLabelling(question)
            ) {
                classNames.push(styles.pbMatchingComponentBody);
            }
            if (customFieldsService.isReading(question)) {
                classNames.push(styles.pbReadingComponentBody);
            }
        }

        if (this.isInQuestionErrorState()) {
            return <QuestionInErrorState question={question} services={services} />;
        }
        return (
            <div
                className={styles.QTIAssessmentItemWrapper}
                ref={this.rootElement}
            >
                {
                    this.shouldRenderAudioFromAlternatives
                        ? this.renderAudioFromChoiceAlternatives(question, choiceSequences)
                        : null
                }
                <div
                    id={`QTI_renderer_${question.identifier}`}
                    className={classNames.join(' ')}
                    lang={lang}
                    dangerouslySetInnerHTML={{ __html: question.componentBody }}
                />
            </div>
        );
    }
}

export default QTIAssessmentItem;
